import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { ArrowLeft, Eye, Sparkles, SquarePen } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { useAI } from '@dashboard/context/AIContext';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useSellerThemes } from '@dashboard/hooks/queries/useThemeQueries';
import { usePreview } from '@dashboard/context/PreviewContext';
import usePage from '@dashboard/hooks/usePage';
import useMountEffect from '@dashboard/hooks/useMountEffect';
import AIEmptyState from './AIEmptyState';
import AITurn from './AITurn';
import Composer from './Composer';
import ResetPageButton from './ResetPageButton';
import GuideCard from './GuideCard';
import GuideList from './GuideList';
import PanelTooltip from './PanelTooltip';
import StarterPrompts from './StarterPrompts';
import { findGuide, guidesFor } from './aiGuides';
import { targetLocation } from './aiChangeLocations';
import { isBlankEditorPage, STARTER_PROMPTS } from './aiStarterPrompts';

// The preview view that shows each step of the buyer's journey; emails have nothing to outline.
const STEP_PREVIEW_TYPES = {
  checkout: 'checkout',
  confirmation: 'confirmationCheckoutMessage',
  upsell: 'upsell',
};

// Children keep their height: a card with overflow-hidden would shrink to fit instead of scrolling.
const SCROLL_AREA_CLASS = 'flex min-h-0 flex-1 flex-col gap-4.5 overflow-y-auto p-5 *:shrink-0';

function lastTurnId(turns, matches) {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (matches(turns[i])) return turns[i].id;
  }
  return null;
}

function PanelHeader({ page, isStreaming, onNewChat, onReset, onPreview }) {
  return (
    <div className="flex h-13 shrink-0 items-center justify-between gap-2 border-b pl-4 pr-2.5">
      <div className="flex items-center gap-2">
        <Sparkles aria-hidden="true" className="size-4" />
        <h2 className="text-sm font-semibold">AI editor</h2>
      </div>
      <div className="flex items-center gap-0.5">
        <Button variant="outline" size="sm" className="mr-1 lg:hidden" onClick={onPreview}>
          <Eye aria-hidden="true" />
          Preview
        </Button>
        <PanelTooltip label="New chat">
          <Button variant="ghost" size="icon" aria-label="Start a new chat" onClick={onNewChat}>
            <SquarePen aria-hidden="true" />
          </Button>
        </PanelTooltip>
        <ResetPageButton page={page} isStreaming={isStreaming} onReset={onReset} />
      </div>
    </div>
  );
}

export default function AIPanel() {
  const {
    turns,
    isStreaming,
    mode,
    setMode,
    guide,
    setGuide,
    draft,
    setDraft,
    sendMessage,
    stop,
    newChat,
    retry,
    showInPreview,
  } = useAI();
  const { page } = usePage();
  const { seller } = useAuth();
  const [preview, previewDispatch] = usePreview();
  const history = useHistory();
  const scrollRef = useRef(null);
  const latestTurnId = lastTurnId(turns, turn => turn.role === 'assistant');
  // A guide turn is last only until a message is sent with it; the guide stays attached after.
  const guideTurn = turns[turns.length - 1]?.role === 'guide' ? turns[turns.length - 1] : null;
  const hasMessages = turns.some(turn => turn.role !== 'guide');
  const { data: themes } = useSellerThemes();
  const guideGroups = guidesFor({ page, seller, themes });
  const composerRef = useRef(null);
  const isNewPage = turns.length === 0 && isBlankEditorPage(page);
  // null follows the chat: the guides show while it is empty, and the chat once it starts.
  // A new page shows its starter prompts instead.
  const [browserChoice, setBrowserChoice] = useState(null);
  const isBrowsing = browserChoice ?? (turns.length === 0 && !isNewPage);
  const [openGuideGroups, setOpenGuideGroups] = useState([]);
  // The empty state's guides sit under its intro, so each browser keeps its own scroll.
  const browserKind = turns.length === 0 ? 'empty' : 'chat';
  const browserScroll = useRef({ empty: 0, chat: 0 });
  const restoreBrowserScroll = useCallback(
    node => {
      if (node) node.scrollTop = browserScroll.current[browserKind];
    },
    [browserKind],
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [turns, isBrowsing]);

  const handleSubmit = (text, attachments) => {
    sendMessage(text, { attachments });
    setDraft('');
    setBrowserChoice(null);
  };

  const handleNewChat = () => {
    newChat();
    setBrowserChoice(null);
    setOpenGuideGroups([]);
    browserScroll.current = { empty: 0, chat: 0 };
  };

  // Back to following the chat, so removing the guide again shows the empty state's guides.
  const openGuide = (key, from) => {
    setGuide(key, { from });
    setBrowserChoice(null);
  };

  const backToGuides = () => {
    setGuide(null);
    setBrowserChoice(true);
  };

  const fillExample = example => {
    flushSync(() => setDraft(example));
    const box = composerRef.current;
    box?.focus();
    box?.setSelectionRange(example.length, example.length);
  };

  // A route is an editor section; a path is a dashboard page outside the editor.
  const handleOpenLocation = location => {
    history.push(
      location.path ?? `/pages/${page._id}/customize${location.route}`,
      location.focus && { aiFocus: location.focus },
    );
  };

  const removeGuide = () => setGuide(null);

  const show = focus => {
    showInPreview(focus);
    const viewType = STEP_PREVIEW_TYPES[focus.step];
    if (viewType && viewType !== preview.type) {
      previewDispatch({ type: 'setViewType', value: viewType });
    }
    // Below lg the preview column only appears in fullscreen, so the scroll would happen off-screen.
    if (window.matchMedia?.('(min-width: 1024px)').matches === false) {
      previewDispatch({ type: 'setFullscreen', value: true });
    }
  };

  const handleShowChange = row =>
    show({
      target: row.location.target,
      fieldId: row.fieldId,
      step: row.location.step,
      op: row.op,
    });

  const handleShowPoint = point =>
    show({
      target: point.target,
      fieldId: point.fieldId,
      step: targetLocation(point.target, page.type).step,
    });

  const handleFixPoint = point => {
    setMode('edit');
    sendMessage(point.fix, { mode: 'edit' });
  };

  const buildExample = prompt => {
    setMode('edit');
    sendMessage(prompt, { mode: 'edit' });
    setDraft('');
    setBrowserChoice(null);
  };

  // Cleared before sending, so a reload or Strict Mode's second mount does not send it again.
  useMountEffect(() => {
    const { aiPrompt, ...state } = history.location.state ?? {};
    if (!aiPrompt) return;
    history.replace({ ...history.location, state });
    setMode('edit');
    sendMessage(aiPrompt, { mode: 'edit', keepLook: true });
  });

  const guideList = (
    <GuideList
      groups={guideGroups}
      openGroups={openGuideGroups}
      onOpenGroupsChange={setOpenGuideGroups}
      onSelect={key => openGuide(key, 'list')}
      onClose={turns.length > 0 ? () => setBrowserChoice(false) : undefined}
    />
  );

  const attachedGuide = guide ? findGuide(guideGroups, guide) : null;
  // Open when just attached, folded once a message is sent. Back returns to the guides it came from.
  const guideCard = attachedGuide && (
    <GuideCard
      key={guideTurn?.id ?? `attached:${guide}`}
      guide={attachedGuide}
      defaultOpen={Boolean(guideTurn)}
      onUseExample={fillExample}
      onBuildExample={buildExample}
      isStreaming={isStreaming}
      onBack={guideTurn?.from === 'list' ? backToGuides : undefined}
      onRemove={removeGuide}
    />
  );

  return (
    <section
      aria-label="AI editor"
      className="flex h-[calc(100vh_-_56px_-_var(--top-banner-h,0px))] flex-col"
    >
      <PanelHeader
        page={page}
        isStreaming={isStreaming}
        onNewChat={handleNewChat}
        onReset={handleNewChat}
        onPreview={() => previewDispatch({ type: 'setFullscreen', value: true })}
      />

      {isBrowsing ? (
        <div
          key={`guides-${browserKind}`}
          ref={restoreBrowserScroll}
          className={SCROLL_AREA_CLASS}
          onScroll={event => {
            browserScroll.current[browserKind] = event.currentTarget.scrollTop;
          }}
        >
          {turns.length === 0 ? (
            <AIEmptyState isNewPage={isNewPage} pageType={page.type} guidesSlot={guideList} />
          ) : (
            guideList
          )}
        </div>
      ) : (
        <div key="chat" ref={scrollRef} className={SCROLL_AREA_CLASS}>
          {!hasMessages && guideTurn?.from !== 'section' ? (
            <AIEmptyState
              isNewPage={isNewPage}
              pageType={page.type}
              guidesSlot={
                isNewPage ? (
                  <StarterPrompts
                    prompts={STARTER_PROMPTS[page.type]}
                    onUse={fillExample}
                    onBuild={buildExample}
                  />
                ) : undefined
              }
            />
          ) : (
            turns.map(turn => (
              <AITurn
                key={turn.id}
                turn={turn}
                pageType={page.type}
                isLatest={turn.id === latestTurnId}
                onFixPoint={handleFixPoint}
                onShowChange={handleShowChange}
                onShowPoint={handleShowPoint}
                onOpenLocation={handleOpenLocation}
                onRetry={retry}
                onNewChat={handleNewChat}
                guide={turn.role === 'guide' ? findGuide(guideGroups, turn.guide) : undefined}
              />
            ))
          )}
        </div>
      )}

      <Composer
        ref={composerRef}
        mode={mode}
        pageType={page.type}
        onModeChange={setMode}
        value={draft}
        onChange={setDraft}
        onSubmit={handleSubmit}
        onStop={stop}
        isStreaming={isStreaming}
        guideCard={guideCard}
        guideGroups={guideGroups}
        onSelectGuide={key => openGuide(key, 'menu')}
        isGuidesOpen={isBrowsing}
        onToggleGuides={() => setBrowserChoice(!isBrowsing)}
      />

      {preview.isFullscreen && (
        // On phones the preview's own toolbar runs off the screen, so the chat offers the way back.
        <Button
          className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 shadow-lg lg:hidden"
          onClick={() => previewDispatch({ type: 'setFullscreen', value: false })}
        >
          <ArrowLeft aria-hidden="true" />
          Back to chat
        </Button>
      )}
    </section>
  );
}
